import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { track } from "@/lib/analytics";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "kaf-install-dismissed-at";
const DISMISS_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

/**
 * Non-intrusive Add-to-Home-Screen prompt. Only appears when the browser
 * actually fires `beforeinstallprompt` (Chromium/Android). Silently ignored
 * on iOS Safari — users install via the share sheet there.
 */
export function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;

    // Already installed / standalone → never prompt.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    if (standalone) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
      setVisible(true);
      try { track("pwa_install_prompt_shown"); } catch {}
    };
    const onInstalled = () => {
      setVisible(false);
      setEvent(null);
      try { track("pwa_install_completed"); } catch {}
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    try { track("pwa_install_dismissed"); } catch {}
  }

  async function install() {
    if (!event) return;
    try {
      await event.prompt();
      const choice = await event.userChoice;
      try { track("pwa_install_choice", { outcome: choice.outcome }); } catch {}
    } finally {
      setVisible(false);
      setEvent(null);
    }
  }

  if (!visible || !event) return null;

  return (
    <div
      role="dialog"
      aria-label="Install KAfrequency"
      className="fixed inset-x-3 bottom-24 z-40 mx-auto max-w-md rounded-2xl border border-gold/40 bg-obsidian/95 p-4 shadow-halo backdrop-blur sm:inset-x-auto sm:right-6 sm:bottom-6"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-obsidian">
          <Download size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base text-ivory">Install KAfrequency</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add the temple to your home screen. Offline access, faster loads, native feel.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={install}
              className="rounded-full bg-gold-gradient px-4 py-1.5 text-xs font-semibold text-obsidian shadow-gold hover:shadow-halo"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-full border hairline px-4 py-1.5 text-xs text-muted-foreground hover:border-gold hover:text-gold"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="rounded-full p-1 text-muted-foreground hover:text-gold"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
